import DOMPurify from 'dompurify'
import { Marked, type Tokens } from 'marked'

/**
 * Model-written Markdown to HTML that is safe to inject. Every chat surface
 * renders model text through here, never through `marked` directly.
 *
 * Model replies quote user text (check-in bodies, place names from Google)
 * and can be steered by injected instructions, so the output is treated as
 * hostile:
 * - raw HTML is escaped and shown as written, never parsed;
 * - images render as their alt text, because an image loads by itself and is
 *   the way injected text would send data out in a URL;
 * - links keep only http(s) and mailto, and open in a new tab;
 * - DOMPurify then enforces the tag and attribute allowlist on whatever the
 *   renderer produced.
 */

const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const SAFE_HREF = /^(https?:|mailto:)/i

type InlineParser = { parser: { parseInline: (t: Tokens.Generic[]) => string } }

/** The renderer overrides both variants share: the guarantees above. */
const safeRenderer = {
    html({ text }: Tokens.HTML | Tokens.Tag) {
        return escapeHtml(text)
    },
    image({ text }: Tokens.Image) {
        return escapeHtml(text)
    },
    link(this: InlineParser, { href, tokens }: Tokens.Link) {
        const inner = this.parser.parseInline(tokens)
        if (!SAFE_HREF.test(href.trim())) return inner
        return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer nofollow">${inner}</a>`
    },
}

const md = new Marked({ gfm: true, breaks: true, renderer: safeRenderer })

const ALLOWED_TAGS = [
    'p', 'br', 'strong', 'em', 'del', 'code', 'pre', 'ul', 'ol', 'li', 'blockquote', 'hr', 'a',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
]
const ALLOWED_ATTR = ['href', 'target', 'rel', 'start', 'align']
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:)/i

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
        node.setAttribute('target', '_blank')
        node.setAttribute('rel', 'noopener noreferrer nofollow')
    }
})

export function renderMarkdown(text: string): string {
    const html = md.parse(text ?? '', { async: false }) as string
    return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, ALLOWED_URI_REGEXP })
}

// -- Chats --------------------------------------------------------------------

/**
 * The Chats page's variant (docs/chats.md §2): the same guarantees, a narrower
 * allowlist, and headings drawn small — a reply's heading separates its parts,
 * it is not a page title. Depth 1–2 renders as h3, 3 and deeper as h4. Its own
 * Marked instance, so no other caller's options reach it.
 */
const chatMd = new Marked({
    gfm: true,
    breaks: true,
    renderer: {
        ...safeRenderer,
        heading(this: InlineParser, { tokens, depth }: Tokens.Heading) {
            const tag = depth <= 2 ? 'h3' : 'h4'
            return `<${tag}>${this.parser.parseInline(tokens)}</${tag}>\n`
        },
    },
})

const CHAT_TAGS = [
    'p', 'br', 'strong', 'em', 'del', 'code', 'pre', 'ul', 'ol', 'li', 'blockquote', 'hr', 'a',
    'h3', 'h4', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
]

const isWord = (c: string | undefined) => !!c && /[\p{L}\p{N}]/u.test(c)
const isSpace = (c: string | undefined) => c === undefined || /\s/.test(c)

/**
 * While a reply streams, close the `**`, `_` and backticks its last paragraph
 * has left open, so bold reads as bold as it arrives instead of as `**` that
 * snaps into bold when the closer lands. A marker with nothing after it yet is
 * dropped rather than closed, since `**` closed at once prints as `****`.
 * A line-start `* ` is a bullet and an intraword `_` part of a name, so
 * neither counts; an open code fence is left alone, since Marked already runs
 * it to the end.
 */
export function closeOpenMarkers(text: string): string {
    if ((text.match(/^\s*(```|~~~)/gm) ?? []).length % 2 === 1) return text
    const start = text.lastIndexOf('\n\n') + 1
    const head = text.slice(0, start)
    let tail = text.slice(start).replace(/\s+$/, '')
    const open: { marker: string; at: number }[] = []
    const top = () => open[open.length - 1]?.marker
    for (let i = 0; i < tail.length; i++) {
        const c = tail[i]
        if (c === '\\') {
            i++
            continue
        }
        if (c === '`') {
            if (top() === '`') open.pop()
            else open.push({ marker: '`', at: i })
            continue
        }
        if (top() === '`') continue
        if (c === '*' && tail[i + 1] === '*') {
            const idx = open.findIndex((o) => o.marker === '**')
            if (idx >= 0) open.splice(idx)
            else open.push({ marker: '**', at: i })
            i++
            continue
        }
        if (c === '_') {
            const idx = open.findIndex((o) => o.marker === '_')
            if (idx >= 0 && !isSpace(tail[i - 1]) && !isWord(tail[i + 1])) open.splice(idx)
            else if (idx < 0 && !isWord(tail[i - 1]) && !isSpace(tail[i + 1])) open.push({ marker: '_', at: i })
        }
    }
    for (const { marker, at } of open.reverse()) {
        if (tail.slice(at + marker.length).trim() === '') tail = tail.slice(0, at).replace(/\s+$/, '')
        else tail += marker
    }
    return head + tail
}

/** A reply that opens with a line holding only a number and a dot is an
 *  answer ("3."), not an empty numbered item starting at 3, which is how
 *  Markdown reads it: gpt-5 answers a count that way. */
const keepBareNumber = (text: string) => text.replace(/^(\s*\d+)([.)])(?=[ \t]*(\n|$))/, '$1\\$2')

export function renderChatMarkdown(text: string, { streaming = false }: { streaming?: boolean } = {}): string {
    const source = keepBareNumber(streaming ? closeOpenMarkers(text ?? '') : (text ?? ''))
    const html = chatMd.parse(source, { async: false }) as string
    return DOMPurify.sanitize(html, { ALLOWED_TAGS: CHAT_TAGS, ALLOWED_ATTR, ALLOWED_URI_REGEXP })
}
